// IndicatorModal üst satırı (spec §5 "Gösterge satırı / IndicatorModal", Promote durum matrisi):
// göstergenin kapsamı ("müşteriye özel" / "katalog"), satırı sete getiren paket ("Kaynak: <paket>
// paketi" — `sourcePackageId`, ad paket önbelleğinden) ve müşteriye özel yükseltilebilir göstergede
// "Kataloğa yükselt…" (kpi.manage; 44 px). Yükseltme sonrası satır "katalog" + seçilen paket(ler)
// + bildirim satırı (`role=status`).

import { useEffect, useRef } from "react";
import { type KpiMetric } from "@mahir/shared";
import { KPI_PACKAGES_COPY, packageNamesText } from "../../lib/kpi-packages-copy";
import { useKpiPackagesStore } from "../../stores/kpi-packages-store";
import { btnGhost } from "./kpi-form-styles";

const P = KPI_PACKAGES_COPY.promote;

/** Yalnız müşteriye özel ∧ saklı `metric` ∧ kırılım üyesi değil (grup/kategori yükseltilmez). */
export function isPromotable(m: KpiMetric | undefined): m is KpiMetric {
  return !!m && m.clientId !== null && (m.nodeKind ?? "metric") === "metric" && !m.dimensionMember;
}

export interface IndicatorSourceRowProps {
  /** göstergenin katalog satırı (bilinmiyorsa kapsam yazılmaz) */
  metric: KpiMetric | undefined;
  sourcePackageId: string | null | undefined;
  /** bu pencerede yükseltildi — seçilen paketler ve yükselen gösterge sayısı */
  promoted: { packageIds: string[]; count: number } | null;
  canPromote: boolean;
  /** sayfada kaydedilmemiş set değişikliği var — yükseltme kilitli */
  locked: boolean;
  onPromote: () => void;
}

export function IndicatorSourceRow(props: IndicatorSourceRowProps): JSX.Element | null {
  const { sourcePackageId, promoted } = props;
  const packages = useKpiPackagesStore((s) => s.packages);
  useEffect(() => {
    if (sourcePackageId || promoted?.packageIds.length)
      void useKpiPackagesStore.getState().loadPackages();
  }, [sourcePackageId, promoted]);
  const sourceIds = sourcePackageId ? [sourcePackageId] : (promoted?.packageIds ?? []);
  const sourceNames = sourceIds
    .map((id) => packages.find((p) => p.id === id)?.name)
    .filter((n): n is string => typeof n === "string");
  return <IndicatorSourceRowView {...props} sourceNames={sourceNames} />;
}

/** Görünüm — paket adları çözülmüş verilir (SSR testleri doğrudan). */
export function IndicatorSourceRowView(
  props: IndicatorSourceRowProps & { sourceNames: readonly string[] },
): JSX.Element | null {
  const { metric, promoted, sourceNames } = props;
  // DR17-5: yükseltme sonrası tetikleyici düğme kalkar — odak bildirim satırına (body'ye düşmesin)
  const doneRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (promoted) doneRef.current?.focus();
  }, [promoted]);
  const scope = promoted
    ? P.scopeCatalog
    : metric
      ? metric.clientId
        ? P.scopeClient
        : P.scopeCatalog
      : null;
  const showButton = !promoted && props.canPromote && isPromotable(metric);
  if (!scope && sourceNames.length === 0 && !showButton) return null;

  return (
    <div style={{ marginBottom: 12 }} data-testid="kpi-indicator-source">
      <div
        className="flex items-center gap-2 text-sm"
        style={{ flexWrap: "wrap", color: "var(--color-text-2)" }}
      >
        {scope ? <span data-testid="kpi-indicator-scope">{scope}</span> : null}
        {sourceNames.length > 0 ? (
          <span>
            {scope ? "· " : ""}
            {KPI_PACKAGES_COPY.hint.source(packageNamesText(sourceNames))}
          </span>
        ) : null}
        {showButton ? (
          <button
            type="button"
            style={{
              ...btnGhost,
              minHeight: 44,
              minWidth: 44,
              marginLeft: "auto",
              fontSize: "0.875rem",
            }}
            disabled={props.locked}
            {...(props.locked
              ? { title: P.saveFirst, "aria-describedby": "kpi-promote-locked" }
              : {})}
            onClick={props.onPromote}
          >
            {P.action}
          </button>
        ) : null}
      </div>
      {showButton && props.locked ? (
        <div id="kpi-promote-locked" className="text-sm" style={{ color: "var(--color-text-3)" }}>
          {P.saveFirst}
        </div>
      ) : null}
      {promoted ? (
        <div
          ref={doneRef}
          tabIndex={-1}
          role="status"
          className="text-sm"
          style={{ color: "var(--color-success)" }}
        >
          {P.done(promoted.count)}
        </div>
      ) : null}
    </div>
  );
}
